import { useEffect, useState } from 'react';
import { 
  Settings as SettingsIcon,
  Shield,
  Image as ImageIcon,
  Globe,
  Palette,
  Save,
  RefreshCw,
  Lock,
  AlertCircle,
  CheckCircle,
  Terminal,
  Info
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { api } from '../api/client';

interface SiteSettings {
  title: string;
  subtitle: string;
  description: string;
  keywords: string;
  logo: string;
  favicon: string;
  icp: string;
}

interface ImageSettings {
  autoBackup: boolean;
  autoVerify: boolean;
  verifyInterval: number;
  cacheTime: number;
  fallbackToR2: boolean;
  maxFileSize: number;
}

interface SecuritySettings {
  jwtExpiresIn: number;
  maxLoginAttempts: number;
  lockoutDuration: number;
}

interface FeatureSettings {
  search: boolean;
  tags: boolean;
  categories: boolean;
  darkMode: boolean;
  comments: boolean;
  analytics: boolean;
}

interface SystemInfo {
  version: string;
  environment: string;
  stats: {
    totalGames: number;
    r2ObjectCount: number;
    r2TotalSize: number;
    kvKeys: number;
  };
  settings: {
    updatedAt: string | null;
  };
}

export function Settings() {
  useAuthStore();
  const [activeTab, setActiveTab] = useState<'site' | 'image' | 'security' | 'features' | 'system'>('site');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  const [site, setSite] = useState<SiteSettings>({
    title: '',
    subtitle: '',
    description: '',
    keywords: '',
    logo: '',
    favicon: '',
    icp: ''
  });
  
  const [image, setImage] = useState<ImageSettings>({
    autoBackup: true,
    autoVerify: true,
    verifyInterval: 24,
    cacheTime: 31536000,
    fallbackToR2: true,
    maxFileSize: 5242880
  });
  
  const [security, setSecurity] = useState<SecuritySettings>({
    jwtExpiresIn: 86400,
    maxLoginAttempts: 5,
    lockoutDuration: 30
  });
  
  const [features, setFeatures] = useState<FeatureSettings>({
    search: true,
    tags: true,
    categories: true,
    darkMode: true,
    comments: false,
    analytics: true
  });
  
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  
  // 密码修改
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordResult, setPasswordResult] = useState<any>(null);

  useEffect(() => {
    fetchSettings();
    fetchSystemInfo();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/admin/settings');
      if (res.data?.success) {
        const { settings } = res.data.data;
        setSite(settings.site);
        setImage(settings.image);
        setSecurity(settings.security);
        setFeatures(settings.features);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemInfo = async () => {
    try {
      const res = await api.get('/admin/system/info');
      if (res.data?.success) {
        setSystemInfo(res.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch system info:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const updateData: Record<string, unknown> = {};
      
      switch (activeTab) {
        case 'site':
          updateData.site = site;
          break;
        case 'image':
          updateData.image = image;
          break;
        case 'security':
          updateData.security = security;
          break;
        case 'features':
          updateData.features = features;
          break;
      }
      
      const res = await api.put('/admin/settings', updateData);
      if (res.data?.success) {
        setMessage({ type: 'success', text: '设置已保存' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '保存失败' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('新密码与确认密码不一致');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      alert('新密码至少需要6个字符');
      return;
    }
    
    try {
      const res = await api.post('/admin/settings/password', {
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword
      });
      
      if (res.data?.success) {
        setPasswordResult(res.data.data);
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      }
    } catch (error: any) {
      alert(error.response?.data?.error?.message || '修改密码失败');
    }
  };

  const handleReset = async () => {
    if (!confirm('确定要重置所有设置为默认值吗？此操作不可恢复。')) return;
    
    try {
      const res = await api.post('/admin/settings/reset', { confirm: true });
      if (res.data?.success) {
        alert('设置已重置为默认值');
        fetchSettings();
      }
    } catch (error) {
      alert('重置失败');
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 标题 */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-white flex items-center gap-2">
          <SettingsIcon className="w-6 h-6" />
          系统设置
        </h1>
        {activeTab !== 'system' && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors disabled:opacity-50"
          >
            <Save className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
            {saving ? '保存中...' : '保存设置'}
          </button>
        )}
      </div>

      {/* 消息提示 */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center gap-2 ${
          message.type === 'success' 
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* 标签页 */}
      <div className="flex flex-wrap gap-2 border-b border-zinc-200 dark:border-zinc-800">
        {[
          { key: 'site', label: '站点信息', icon: Globe },
          { key: 'image', label: '图片设置', icon: ImageIcon },
          { key: 'security', label: '安全设置', icon: Shield },
          { key: 'features', label: '功能开关', icon: Palette },
          { key: 'system', label: '系统信息', icon: Info },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key as any)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === key
                ? 'border-zinc-900 dark:border-white text-zinc-900 dark:text-white'
                : 'border-transparent text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* 站点设置 */}
      {activeTab === 'site' && (
        <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl border border-zinc-200 dark:border-zinc-800 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                站点标题
              </label>
              <input
                type="text"
                value={site.title}
                onChange={e => setSite({ ...site, title: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                副标题
              </label>
              <input
                type="text"
                value={site.subtitle}
                onChange={e => setSite({ ...site, subtitle: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              站点描述
            </label>
            <textarea
              value={site.description}
              onChange={e => setSite({ ...site, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              关键词（用逗号分隔）
            </label>
            <input
              type="text"
              value={site.keywords}
              onChange={e => setSite({ ...site, keywords: e.target.value })}
              className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                Logo URL
              </label>
              <input
                type="text"
                value={site.logo}
                onChange={e => setSite({ ...site, logo: e.target.value })}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                Favicon URL
              </label>
              <input
                type="text"
                value={site.favicon}
                onChange={e => setSite({ ...site, favicon: e.target.value })}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              ICP备案号
            </label>
            <input
              type="text"
              value={site.icp}
              onChange={e => setSite({ ...site, icp: e.target.value })}
              placeholder="京ICP备xxxxxxxx号"
              className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
            />
          </div>
        </div>
      )}

      {/* 图片设置 */}
      {activeTab === 'image' && (
        <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl border border-zinc-200 dark:border-zinc-800 space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-zinc-100 dark:border-zinc-800">
            <div>
              <h3 className="font-medium text-zinc-900 dark:text-white">自动备份</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">新图片自动备份到 R2</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={image.autoBackup}
                onChange={e => setImage({ ...image, autoBackup: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-zinc-300 dark:peer-focus:ring-zinc-800 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-zinc-900 dark:peer-checked:bg-white"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between py-3 border-b border-zinc-100 dark:border-zinc-800">
            <div>
              <h3 className="font-medium text-zinc-900 dark:text-white">自动验证</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">定期检查外链有效性</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={image.autoVerify}
                onChange={e => setImage({ ...image, autoVerify: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-zinc-300 dark:peer-focus:ring-zinc-800 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-zinc-900 dark:peer-checked:bg-white"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between py-3 border-b border-zinc-100 dark:border-zinc-800">
            <div>
              <h3 className="font-medium text-zinc-900 dark:text-white">R2 自动回退</h3>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">外链失败时自动使用 R2 备份</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={image.fallbackToR2}
                onChange={e => setImage({ ...image, fallbackToR2: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-zinc-300 dark:peer-focus:ring-zinc-800 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-zinc-900 dark:peer-checked:bg-white"></div>
            </label>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                验证间隔（小时）
              </label>
              <input
                type="number"
                value={image.verifyInterval}
                onChange={e => setImage({ ...image, verifyInterval: parseInt(e.target.value) })}
                min={1}
                max={168}
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                缓存时间（秒）
              </label>
              <input
                type="number"
                value={image.cacheTime}
                onChange={e => setImage({ ...image, cacheTime: parseInt(e.target.value) })}
                min={0}
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                最大文件大小（字节）
              </label>
              <input
                type="number"
                value={image.maxFileSize}
                onChange={e => setImage({ ...image, maxFileSize: parseInt(e.target.value) })}
                min={1024}
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* 安全设置 */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          {/* JWT 设置 */}
          <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl border border-zinc-200 dark:border-zinc-800 space-y-4">
            <h3 className="font-medium text-zinc-900 dark:text-white flex items-center gap-2">
              <Shield className="w-5 h-5" />
              JWT 设置
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  Token 有效期（秒）
                </label>
                <input
                  type="number"
                  value={security.jwtExpiresIn}
                  onChange={e => setSecurity({ ...security, jwtExpiresIn: parseInt(e.target.value) })}
                  min={300}
                  className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
                />
                <p className="text-xs text-zinc-500 mt-1">默认 86400 = 24小时</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  最大登录尝试次数
                </label>
                <input
                  type="number"
                  value={security.maxLoginAttempts}
                  onChange={e => setSecurity({ ...security, maxLoginAttempts: parseInt(e.target.value) })}
                  min={1}
                  max={10}
                  className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  锁定时间（分钟）
                </label>
                <input
                  type="number"
                  value={security.lockoutDuration}
                  onChange={e => setSecurity({ ...security, lockoutDuration: parseInt(e.target.value) })}
                  min={5}
                  className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
                />
              </div>
            </div>
          </div>
          
          {/* 修改密码 */}
          <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl border border-zinc-200 dark:border-zinc-800 space-y-4">
            <h3 className="font-medium text-zinc-900 dark:text-white flex items-center gap-2">
              <Lock className="w-5 h-5" />
              修改管理员密码
            </h3>
            
            <div className="space-y-3">
              <input
                type="password"
                value={passwordForm.oldPassword}
                onChange={e => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                placeholder="当前密码"
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={e => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                placeholder="新密码"
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={e => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                placeholder="确认新密码"
                className="w-full px-3 py-2 bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-lg"
              />
              <button
                onClick={handleChangePassword}
                className="w-full py-2 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors"
              >
                修改密码
              </button>
            </div>
            
            {passwordResult && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300 mb-2">{passwordResult.message}</p>
                <div className="flex items-center gap-2 p-2 bg-zinc-900 dark:bg-zinc-800 rounded text-xs font-mono text-zinc-100 overflow-x-auto">
                  <Terminal className="w-4 h-4 shrink-0" />
                  <code>{passwordResult.command}</code>
                </div>
                <p className="text-xs text-zinc-500 mt-2">{passwordResult.tip}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 功能开关 */}
      {activeTab === 'features' && (
        <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl border border-zinc-200 dark:border-zinc-800 space-y-2">
          {[
            { key: 'search', label: '搜索功能', desc: '允许用户搜索游戏' },
            { key: 'tags', label: '标签系统', desc: '显示游戏标签' },
            { key: 'categories', label: '分类导航', desc: '按分类浏览游戏' },
            { key: 'darkMode', label: '深色模式', desc: '允许切换深色主题' },
            { key: 'comments', label: '评论功能', desc: '允许用户评论（需额外配置）' },
            { key: 'analytics', label: '访问统计', desc: '记录浏览和点击数据' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between py-3 border-b border-zinc-100 dark:border-zinc-800 last:border-0">
              <div>
                <h3 className="font-medium text-zinc-900 dark:text-white">{label}</h3>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">{desc}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={features[key as keyof FeatureSettings]}
                  onChange={e => setFeatures({ ...features, [key]: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-zinc-300 dark:peer-focus:ring-zinc-800 rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-zinc-900 dark:peer-checked:bg-white"></div>
              </label>
            </div>
          ))}
        </div>
      )}

      {/* 系统信息 */}
      {activeTab === 'system' && systemInfo && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl border border-zinc-200 dark:border-zinc-800">
            <h3 className="font-medium text-zinc-900 dark:text-white mb-4">版本信息</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">系统版本</p>
                <p className="text-lg font-medium text-zinc-900 dark:text-white">{systemInfo.version}</p>
              </div>
              <div>
                <p className="text-sm text-zinc-500 dark:text-zinc-400">运行环境</p>
                <p className="text-lg font-medium text-zinc-900 dark:text-white capitalize">{systemInfo.environment}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl border border-zinc-200 dark:border-zinc-800">
            <h3 className="font-medium text-zinc-900 dark:text-white mb-4">数据统计</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                <p className="text-sm text-zinc-500 dark:text-zinc-400">游戏总数</p>
                <p className="text-2xl font-bold text-zinc-900 dark:text-white">{systemInfo.stats.totalGames}</p>
              </div>
              <div className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                <p className="text-sm text-zinc-500 dark:text-zinc-400">R2 对象数</p>
                <p className="text-2xl font-bold text-zinc-900 dark:text-white">{systemInfo.stats.r2ObjectCount}</p>
              </div>
              <div className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                <p className="text-sm text-zinc-500 dark:text-zinc-400">R2 存储</p>
                <p className="text-2xl font-bold text-zinc-900 dark:text-white">{formatSize(systemInfo.stats.r2TotalSize)}</p>
              </div>
              <div className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg">
                <p className="text-sm text-zinc-500 dark:text-zinc-400">KV 键数</p>
                <p className="text-2xl font-bold text-zinc-900 dark:text-white">{systemInfo.stats.kvKeys}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl border border-zinc-200 dark:border-zinc-800">
            <h3 className="font-medium text-zinc-900 dark:text-white mb-4">操作</h3>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
              >
                重置所有设置为默认
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
